// Moje školení (15. 10. 2026, schválený mockup): co mi bylo přiděleno (videa, prezentace,
// dokumenty) a moje testy. Po „Mám hotovo“ se nabídne navazující test, má-li ho školení.
import React, { useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { GraduationCap, Play, FileText, BookOpen, Lock } from "lucide-react";
import GradientHeader from "../components/GradientHeader.jsx";
import { Stranka, EmptyState } from "../components/ui.jsx";
import { Pil } from "../components/sales/salesUi.jsx";
import { toast } from "../components/Toast.jsx";
import { apiFetch, useApiData } from "../lib/api.js";
import { PrepinacDlazdice } from "./hr/SkoleniMaterialy.jsx";
import SkoleniKarta, { Stitek, PruhPostupu, datumCz } from "../components/SkoleniKarta.jsx";
import SkoleniOkno from "../components/SkoleniOkno.jsx";
import { slozModuly } from "../lib/skoleniVideo.js";
import TrenerDokumenty from "./trener/Dokumenty.jsx";

const sklon = (n, a, b, c) => (n === 1 ? a : n >= 2 && n <= 4 ? b : c);

export default function MojeSkoleni() {
  const { data, loading, refetch } = useApiData("/api/skoleni-materialy?moje=1", { bezKese: true });
  // V trenérském portálu (/trener/skoleni) je třetí dlaždice Dokumenty (16. 10. 2026, schválený mockup).
  const jeTrener = useLocation().pathname.startsWith("/trener");
  const [params] = useSearchParams();
  const [sekce, setSekce] = useState(["testy", "dokumenty"].includes(params.get("sekce")) ? params.get("sekce") : "skoleni");
  const [otevreno, setOtevreno] = useState(null);
  const polozky = data?.polozky || [];
  const testy = data?.testy || [];
  const novych = polozky.filter((p) => !p.parent_id && !p.hotovo_at).length;
  const cekaTestu = testy.filter((t) => !t.finished_at).length;
  const zamcene = data?.zamcene || [];
  const testySkupiny = useMemo(() => {
    const m = new Map();
    for (const t of testy) { const k = t.okruh || "Ostatní testy"; if (!m.has(k)) m.set(k, { testy: [], zamcene: [] }); m.get(k).testy.push(t); }
    for (const z of zamcene) { const k = z.okruh; if (!m.has(k)) m.set(k, { testy: [], zamcene: [] }); m.get(k).zamcene.push(z); }
    return [...m.entries()];
  }, [testy, zamcene]);

  const skupiny = useMemo(() => {
    const m = new Map();
    for (const p of polozky) { if (!m.has(p.kategorie)) m.set(p.kategorie, []); m.get(p.kategorie).push(p); }
    return [...m.entries()];
  }, [polozky]);

  async function akce(p, typ) {
    try {
      const r = await apiFetch("/api/skoleni-materialy", { method: "POST", body: JSON.stringify({ akce: typ, id: p.id }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Nepodařilo se uložit.");
      if (typ === "hotovo") toast(j.test_token ? "Hotovo. Teď tě čeká navazující test." : "Hotovo.", "success");
      refetch();
    } catch (e) { toast(e.message, "error"); }
  }
  const otevrenaSkupina = skupiny.find(([k]) => k === otevreno);

  return (
    <Stranka sirka="uzka">
      <GradientHeader title="Moje školení" icon={GraduationCap} />
      <PrepinacDlazdice hodnota={sekce} onChange={setSekce} polozky={[
        { key: "skoleni", nazev: "Školení", ikona: Play, pod: data ? (novych ? `${novych} ${sklon(novych, "nové", "nová", "nových")}` : "vše hotovo") : "" },
        { key: "testy", nazev: "Testy", ikona: FileText, pod: data ? (cekaTestu ? `${cekaTestu} ${sklon(cekaTestu, "čeká", "čekají", "čeká")}` : "nic nečeká") : "" },
        ...(jeTrener ? [{ key: "dokumenty", nazev: "Dokumenty", ikona: BookOpen, pod: "manuály, procesy a návody" }] : []),
      ]} />

      {sekce === "dokumenty" ? <TrenerDokumenty bezHlavicky /> : loading && !data ? <div className="h-24 animate-pulse rounded-2xl bg-brand-light" /> : sekce === "skoleni" ? (
        !polozky.length ? <div className="glass"><EmptyState icon={GraduationCap} title="Nic přiděleného" hint="Až ti někdo přidělí školení, uvidíš ho tady." /></div> : (
          <div className="grid gap-4 lg:grid-cols-2">
            {skupiny.map(([kat, ps]) => {
              const moduly = slozModuly(ps);
              const hotovo = moduly.filter((m) => m.hotovo_at).length;
              const dalsi = moduly.find((m) => !m.hotovo_at);
              const priloh = ps.length - moduly.length;
              const zmena = ps.reduce((x, p) => (p.updated_at > x ? p.updated_at : x), "");
              return (
                <SkoleniKarta key={kat} nazev={kat} onOtevrit={() => setOtevreno(kat)}
                  tlacitko={hotovo === 0 ? "Začít" : hotovo === moduly.length ? "Znovu otevřít" : "Pokračovat"}
                  stitky={<><Stitek>{moduly.length} {sklon(moduly.length, "modul", "moduly", "modulů")}</Stitek>{priloh > 0 && <Stitek>{priloh} {sklon(priloh, "příloha", "přílohy", "příloh")}</Stitek>}{zmena && <Stitek>Změněno {datumCz(zmena)}</Stitek>}</>}>
                  <PruhPostupu hotovo={hotovo} celkem={moduly.length} />
                  <div className="text-[13px] text-slate-600">{dalsi ? <>Další: <b className="text-slate-800">{dalsi.nazev}</b></> : "Vše hotovo."}</div>
                </SkoleniKarta>
              );
            })}
          </div>
        )
      ) : (
        !testy.length ? <div className="glass"><EmptyState icon={FileText} title="Žádný test" hint="Až ti někdo přiřadí test, uvidíš ho tady. Test k modulu se otevře po tlačítku Mám hotovo." /></div> : (
          <div className="space-y-4">
            {testySkupiny.map(([okruh, g]) => (
              <div key={okruh} className="glass min-w-0 overflow-hidden !p-0">
                <div className="flex items-center gap-2 bg-brand-light px-4 py-3 font-bold text-brand-700">
                  <span className="flex-1">{okruh}</span>
                  <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold">{g.testy.length + g.zamcene.length}</span>
                </div>
                {g.testy.map((t) => (
                  <div key={t.token} className="flex items-center gap-3 border-t border-slate-100 bg-white px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold leading-snug text-slate-900">{t.nazev}</div>
                      {t.pocet_otazek ? <div className="text-[13px] text-slate-500">{t.pocet_otazek} otázek, {t.limit_min} min</div> : null}
                      <div className="mt-1">{t.finished_at ? <Pil ton="ok" casti={`hotovo${t.procent != null ? `, ${t.procent} %` : ""}`} mala /> : <Pil ton="pozor" casti="čeká na vyplnění" mala />}</div>
                    </div>
                    {!t.finished_at && <a className="btn btn-primary" href={`/skoleni/${t.token}`} target="_blank" rel="noopener noreferrer">Spustit</a>}
                  </div>
                ))}
                {g.zamcene.map((z) => (
                  <div key={z.nazev} className="flex items-center gap-3 border-t border-slate-100 bg-white px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold leading-snug text-slate-900">{z.nazev}</div>
                      <div className="text-[13px] text-slate-500">{z.pocet_otazek} otázek, {z.limit_min} min. Otevře se po testech ke všem modulům.</div>
                      <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600"><Lock className="h-3 w-3" /> zamčeno</div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )
      )}
      {otevrenaSkupina && (
        <SkoleniOkno nazev={otevrenaSkupina[0]} moduly={slozModuly(otevrenaSkupina[1])} rezim="trener"
          onClose={() => setOtevreno(null)}
          onOtevreno={(p) => akce(p, "otevreno")}
          onHotovo={(p) => akce(p, "hotovo")} />
      )}
    </Stranka>
  );
}
